import reactCSS from "reactcss";
import Color from "color";
import { TEXT_DARK_THEME_COLOR, TEXT_LIGHT_THEME_COLOR } from "common/constants";
import { CheckoutParams } from "common/types";
import { FeatureFlag } from "common/utils/environment";
import { isNil, merge } from "lodash";

import { darkThemeColors, defaultColors } from "../colors";
import { PatchedCheckoutInfo } from "../redux/models/configuration";
import { StateShape } from "../redux/store";

interface LayoutColor {
    textColor: string;
    backgroundColor: string;
    backgroundHoverColor?: string;
}

const highlightColor = (colorHex: string, darkTheme = false): string => {
    const color = parseColor(colorHex) ?? (darkTheme ? Color("#000000") : Color("#FFFFFF"));
    const hoverColor = darkTheme ? color.lighten(0.1) : color.darken(0.1);

    return hoverColor.hex();
};

const parseColor = (colorHex?: string) => {
    if (isNil(colorHex)) {
        return null;
    }

    try {
        return Color(colorHex);
    } catch {
        return null;
    }
};

const isSameColor = (color1?: string, color2?: string): boolean => {
    if (color1 === color2) {
        return true;
    }

    const parsedColor1 = parseColor(color1);
    const parsedColor2 = parseColor(color2);

    return !!parsedColor1 && !!parsedColor2 && parsedColor1.hex() === parsedColor2.hex();
};

const getContrastingTextColor = (colorHex: string): string | undefined => {
    const color = parseColor(colorHex);

    return color ? (color.isDark() ? TEXT_DARK_THEME_COLOR : TEXT_LIGHT_THEME_COLOR) : undefined;
};

export const getThemeColors = (data: PatchedCheckoutInfo, applicationParams?: CheckoutParams) => {
    const defaultTheme = (() => {
        if (isNil(applicationParams?.dark)) {
            return data?.theme?.colors;
        }

        // Theme is forced to follow the given color schema instead of the default theming
        return applicationParams.dark ? darkThemeColors : defaultColors;
    })();

    const mainColor = applicationParams?.headerBackgroundColor;
    const buttonBackground = FeatureFlag.buttonBackgroundColorSplit()
        ? applicationParams?.buttonBackgroundColor
        : mainColor;
    const subHeaderColor = applicationParams?.subHeaderColor;
    const subHeaderTextColor = isNil(subHeaderColor) ? undefined : getContrastingTextColor(subHeaderColor);

    return merge({}, defaultTheme, {
        mainColor,
        buttonBackground,
        mainText: isNil(mainColor) ? undefined : getContrastingTextColor(mainColor),
        buttonText: isNil(buttonBackground) ? undefined : getContrastingTextColor(buttonBackground),
        secondaryBackground: subHeaderColor,
        primaryText: subHeaderTextColor,
        secondaryText: subHeaderTextColor,
    });
};

export const getStepTitleColors = (data: PatchedCheckoutInfo, applicationParams?: CheckoutParams) => {
    const { baseText } = getThemeColors(data, applicationParams);

    return { textColor: baseText };
};

/**
 * Compute button color based on the correct parameter, defaulting to the other themes in case of mismatches for the button to be visible
 */
const getButtonBaseColors = (
    data: PatchedCheckoutInfo,
    darkTheme: boolean,
    applicationParams?: CheckoutParams
): Partial<LayoutColor> => {
    const colors = getThemeColors(data, applicationParams);

    const {
        // Button specific colors with highest priority
        buttonText,
        buttonBackground,

        // Header color which historically was the button colors. Taking second priority.
        mainText,
        mainColor,

        // In case none of the above colors worked, use the sub title color to avoid blank button
        mainBackground,
        primaryText,
        secondaryBackground,
    } = colors;

    if (!!buttonBackground && !!buttonText && !isSameColor(mainColor, buttonBackground)) {
        return { textColor: buttonText, backgroundColor: buttonBackground };
    }

    if (!!mainColor && !!mainText && !isSameColor(mainColor, mainBackground)) {
        return { textColor: mainText, backgroundColor: mainColor };
    }

    if (!isSameColor(secondaryBackground, mainBackground)) {
        return { textColor: primaryText, backgroundColor: secondaryBackground };
    }

    return { textColor: mainText, backgroundColor: highlightColor(mainColor, darkTheme) };
};

export const getButtonColors = (
    data: PatchedCheckoutInfo,
    darkTheme: boolean,
    applicationParams?: CheckoutParams
): LayoutColor => {
    const { textColor, backgroundColor } = getButtonBaseColors(data, darkTheme, applicationParams);
    const backgroundHoverColor = highlightColor(backgroundColor);

    return { textColor, backgroundColor, backgroundHoverColor };
};

export const getContentColors = (data: PatchedCheckoutInfo, applicationParams?: CheckoutParams): LayoutColor => {
    const { mainBackground, baseText } = getThemeColors(data, applicationParams);

    return { textColor: baseText, backgroundColor: mainBackground };
};

export const isDarkTheme = (state: StateShape) => state.application.params.params.dark ?? state.configuration.darkTheme;

export const getPrimaryStyle = ({ textColor, backgroundColor, backgroundHoverColor }: LayoutColor, hover: boolean) =>
    reactCSS(
        {
            default: {
                button: {
                    borderColor: backgroundColor,
                    backgroundColor,
                    color: textColor,
                },
            },
            hover: {
                button: {
                    borderColor: backgroundHoverColor,
                    backgroundColor: backgroundHoverColor,
                    color: textColor,
                },
            },
        },
        { hover }
    );

export const getOutlineStyle = (
    { textColor, backgroundColor, backgroundHoverColor }: LayoutColor,
    hover: boolean,
    darkTheme: boolean
) =>
    reactCSS(
        {
            default: {
                button: {
                    backgroundColor: "transparent",
                    borderColor: backgroundColor,
                    color: darkTheme ? TEXT_DARK_THEME_COLOR : TEXT_LIGHT_THEME_COLOR,
                },
            },
            hover: {
                button: {
                    borderColor: backgroundHoverColor,
                    backgroundColor: backgroundHoverColor,
                    color: textColor,
                },
            },
        },
        { hover }
    );
